// Keeps the server-rendered page in step with cic-nav after sign-out.
//
// The header decides "signed in" client-side (a silent Auth0 token), while server-rendered content
// (e.g. the Benefits Snapshot pods) decides from the CpgSiteUser cookie. If the page was rendered
// for a signed-in user but the client can no longer get a token, the two disagree: Sign In in the
// header, signed-in pods below. That happens when the browser replays a stored signed-in page
// (Back/Forward), or when the cookie outlived the Auth0 session. Either way the client is right, so
// we clear the server cookie and reload once to re-render signed out.
//
// The mirror image of prime-and-reload, which handles the server lagging BEHIND a sign-in.

import { isServerSessionEstablished } from './prime-and-reload';

const GUARD_KEY = 'cpg.serverSessionSync';

const readGuard = (): boolean => {
	try {
		return sessionStorage.getItem(GUARD_KEY) === '1';
	} catch {
		return false;
	}
};

const writeGuard = (on: boolean): void => {
	try {
		if (on) {
			sessionStorage.setItem(GUARD_KEY, '1');
		} else {
			sessionStorage.removeItem(GUARD_KEY);
		}
	} catch { /* storage unavailable: worst case is one extra reload attempt per load */ }
};

/**
 * True when the server rendered this page signed in but the client's silent token attempt has
 * FINISHED without a token. `tokenError` is the settled signal: while the attempt is still in
 * flight both values are empty, and treating that as "signed out" would sign out every user
 * on every page load.
 */
export const isServerSessionStale = (accessToken: string | null, tokenError: string | null): boolean =>
	!accessToken && !!tokenError && isServerSessionEstablished();

/**
 * Clears the server cookie and reloads, at most once per tab until the page renders consistently
 * again. The guard stops a loop if the DELETE fails and the server keeps rendering signed in.
 */
export const syncStaleServerSession = async (): Promise<void> => {
	if (readGuard()) {
		return;
	}
	writeGuard(true);
	try {
		await fetch('/api/auth/site-session', { method: 'DELETE', credentials: 'same-origin' });
	} catch { /* non-fatal: the reload still shows whatever the server now thinks */ }
	window.location.reload();
};

/** Call once the page renders consistently, so a later mismatch in this tab can sync again. */
export const resetServerSessionSync = (): void => writeGuard(false);

/**
 * A page restored from the back/forward cache keeps its old JavaScript state, so cic-nav does not
 * re-check the token. If the server rendered it signed in, reload so the server and the client both
 * re-decide from the current session. (Signed-in pages are sent no-store, which normally keeps them
 * out of that cache; this covers browsers that keep them anyway.)
 */
export const reloadIfRestoredSignedIn = (event: PageTransitionEvent): void => {
	if (event.persisted && isServerSessionEstablished()) {
		window.location.reload();
	}
};
